import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Toaster, toast } from "sonner";
import AppRoutes from "./routes/AppRoutes";
import Loader from "./components/Loader";
import { socket } from "./socket";

// --- OFFLINE SCREEN COMPONENT ---
function OfflineScreen() {
  const [dots, setDots] = useState(".");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "." : d + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={styles.offlineContainer}>
      <div style={styles.offlineCard}>
        <div style={styles.iconWrapper}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path
              d="M8 20 C18 10, 46 10, 56 20"
              stroke="#e2e8f0"
              strokeWidth="4"
              strokeLinecap="round"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M14 28 C20 22, 44 22, 50 28"
              stroke="#e2e8f0"
              strokeWidth="4"
              strokeLinecap="round"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M20 36 C24 32, 40 32, 44 36"
              stroke="#e2e8f0"
              strokeWidth="4"
              strokeLinecap="round"
              fill="none"
              opacity="0.3"
            />
            <circle cx="32" cy="44" r="4" fill="#e2e8f0" opacity="0.3" />
            <line
              x1="12"
              y1="12"
              x2="52"
              y2="52"
              stroke="#f97316"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <line
              x1="52"
              y1="12"
              x2="12"
              y2="52"
              stroke="#f97316"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <h1 style={styles.title}>No Internet Connection</h1>
        <p style={styles.subtitle}>Waiting for network{dots}</p>
        <p style={styles.hint}>
          Check your Wi-Fi or mobile data and try again.
        </p>
        <button
          style={styles.retryBtn}
          onClick={() => window.location.reload()}
          onMouseEnter={(e) => (e.target.style.background = "#ea6a00")}
          onMouseLeave={(e) => (e.target.style.background = "#f97316")}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

const styles = {
  offlineContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    fontFamily: "'Inter', sans-serif",
    padding: "20px",
  },
  offlineCard: {
    textAlign: "center",
    padding: "48px 40px",
    borderRadius: "24px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    backdropFilter: "blur(12px)",
    maxWidth: "360px",
    width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
  },
  iconWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#f1f5f9",
    margin: "0 0 8px 0",
    letterSpacing: "-0.3px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#94a3b8",
    margin: "0 0 12px 0",
    fontWeight: "500",
    minHeight: "20px",
  },
  hint: {
    fontSize: "13px",
    color: "#64748b",
    margin: "0 0 32px 0",
    lineHeight: "1.5",
  },
  retryBtn: {
    background: "#f97316",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "14px 40px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s ease",
    width: "100%",
    letterSpacing: "0.3px",
  },
};

// --- MAIN APP ---
function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [appInitializing, setAppInitializing] = useState(() => {
    return !sessionStorage.getItem("appInitialized");
  });

  // --- OFFLINE DETECTION ---
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // --- SPLASH SCREEN LOGIC ---
  useEffect(() => {
    if (!appInitializing) return;

    const timer = setTimeout(() => {
      sessionStorage.setItem("appInitialized", "true");
      setAppInitializing(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [appInitializing]);

  // --- 1. GPS WATCHER (Isolated Context) ---
  const watchIdRef = useRef(null);

  const startLocationWatch = useCallback(async () => {
    if (!("geolocation" in navigator)) {
      console.warn("Location not supported on this device");
      return;
    }

    if (navigator.permissions) {
      try {
        const status = await navigator.permissions.query({
          name: "geolocation",
        });
        if (status.state === "denied") {
          toast.error("Please enable Location access to book rides.");
          return;
        }
      } catch (e) {
        console.warn("Permission check failed");
      }
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        if (watchIdRef.current) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }

        watchIdRef.current = navigator.geolocation.watchPosition(
          () => {
            toast.dismiss("gps-error");
          },
          (err) => {
            let msg = "Unable to fetch location.";
            if (err.code === 1)
              msg = "Please allow Location Permission to continue.";
            if (err.code === 2) msg = "Location is OFF. Please turn it on.";
            toast.error(msg, { id: "gps-error", duration: 4000 });
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
      },
      (err) => {
        let msg = "Location error.";
        if (err.code === 1) msg = "Please enable Location Services.";
        toast.error(msg, { id: "gps-error", duration: 4000 });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, []);

  // Initialize GPS safely
  useEffect(() => {
    startLocationWatch();
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [startLocationWatch]);

  // --- 2. SOCKET & REAL-TIME UPDATES (Isolated Context) ---
  useEffect(() => {
    socket.connect();
    const myCustomerId = localStorage.getItem("customerId");

    // Fired initially AND whenever the app reconnects to the internet
    const onConnect = () => {
      // console.log("Customer App Connected! ID:", socket.id);
      if (myCustomerId) {
        socket.emit("join_customer_session", myCustomerId);
      }
    };

    const handleDriverFound = (data) => {
      if (data.customerId && data.customerId !== myCustomerId) return;

      toast.success("Driver Found! 🚖", {
        description: `${data.driverName || "A driver"} is on the way.`,
        duration: 6000,
        action: {
          label: "TRACK",
          onClick: () => (window.location.href = `/trip/${data.tripId}`),
        },
      });
    };

    // Attach listeners
    socket.on("connect", onConnect);
    socket.on("driver_assigned", handleDriverFound);

    return () => {
      // Cleanup
      socket.off("connect", onConnect);
      socket.off("driver_assigned", handleDriverFound);
      socket.disconnect();
    };
  }, []);

  // --- 3. RENDER SCREENS ---
  if (!isOnline) {
    return <OfflineScreen />;
  }

  if (appInitializing) {
    return <Loader message="STARTING YOUR JOURNEY..." />;
  }

  return (
    <Router>
      <Toaster
        position="top-center"
        theme="light"
        toastOptions={{
          style: {
            marginTop: "max(env(safe-area-inset-top), 15px)",
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            boxShadow:
              "0 16px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)",
            borderRadius: "100px",
            padding: "14px 24px",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1c1c1e",
            letterSpacing: "-0.2px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          },
          duration: 2000,
        }}
      />
      <AppRoutes />
    </Router>
  );
}

export default App;